import React, { ReactNode } from 'react'

import { LightboxImage } from '../LightboxImage/LightboxImage'

/** What the still IS, as Python's one resolver stamps it (`RankItem.previewKind`). */
export type AssetFrameKind = 'image' | 'snapshot' | 'video'

export interface AssetFrameProps {
  url: string
  kind: AssetFrameKind
  alt: string
  /** Sizing comes from the caller (width, height or aspectRatio); the frame fills it. */
  style?: React.CSSProperties
  className?: string
  radius?: number
  /** The picture opens the full still in the lightbox (interactive surfaces only). */
  lightbox?: boolean
  /** A video's play glyph, in px; 0 draws none (thumbnails too small to carry one). */
  playSize?: number
  loading?: 'eager' | 'lazy'
  'data-testid'?: string
  pictureTestId?: string
  playTestId?: string
  /** Overlays drawn on top of the picture: chips, the ⤢ affordance. */
  children?: ReactNode
}

function PlayGlyph(props: { size: number; testId?: string }) {
  const { size, testId } = props

  return (
    <div
      data-testid={testId}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: size,
        height: size,
        borderRadius: 999,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'block',
          marginLeft: Math.round(size * 0.08),
          borderStyle: 'solid',
          borderWidth: `${Math.round(size * 0.16)}px 0 ${Math.round(size * 0.16)}px ${Math.round(size * 0.26)}px`,
          borderColor: 'transparent transparent transparent #FFFFFF',
        }}
      />
    </div>
  )
}

/**
 * One tested asset's still in a frame of the caller's shape, over a blurred,
 * dimmed copy of itself so the frame always looks full. A creative (`image`,
 * `video`) is contain-fit: nothing is ever cut. A landing page (`snapshot`,
 * its top as Python resolved it) fills the frame's width from the top, so
 * only its bottom is ever cut, the one crop the owner asked for; its sides,
 * where a page's headline and logo sit, never are. `data-preview-kind` names
 * which rule drew it.
 */
export function AssetFrame(props: AssetFrameProps) {
  const {
    url,
    kind,
    alt,
    style,
    className,
    radius = 10,
    lightbox = false,
    playSize = 0,
    loading = 'lazy',
    pictureTestId,
    playTestId,
    children,
  } = props

  const pictureStyle: React.CSSProperties = kind === 'snapshot'
    ? { position: 'absolute', top: 0, left: 0, width: '100%', height: 'auto', display: 'block' }
    : { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', objectFit: 'contain', objectPosition: 'center' }

  return (
    <div
      data-testid={props['data-testid']}
      data-preview-kind={kind}
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: radius,
        background: 'var(--slide-neutral-light, #EBEBEB)',
        ...style,
      }}
    >
      <img
        src={url}
        alt=""
        aria-hidden="true"
        loading={loading}
        data-testid="asset-frame-fill"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'blur(16px) brightness(0.72)',
          transform: 'scale(1.15)',
        }}
      />
      {lightbox ? (
        <LightboxImage src={url} alt={alt} style={pictureStyle} data-testid={pictureTestId} />
      ) : (
        <img src={url} alt={alt} loading={loading} data-testid={pictureTestId} style={pictureStyle} />
      )}
      {kind === 'video' && playSize > 0 && <PlayGlyph size={playSize} testId={playTestId} />}
      {children}
    </div>
  )
}
